import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import "../componentCss/webform.css";
import { Link, useParams, useNavigate } from "react-router-dom";
import Dashboardheader from "./dashboardheader";
import Sidebar from "./sidebar";
import "../componentCss/sidebar.css";
import {
  getFormDetailsAction,
  insertFormDataAction,
  getFormDataByMasterAction
} from "../../Action/admin.action";
import toast from 'react-hot-toast';
import { BsArrowLeftCircleFill } from "react-icons/bs";
import config from "../../config/config";
import Sketch from "./Sketch";
import { decryptData } from "./decrypt";
import Select from "react-select";
import { evaluateFormula, parseFormulaFields } from "../../utils/formulaBuilder";
import formTemplateService from "../../services/formTemplateService";
import { SignatureField } from './FormFields/EnhancedFieldTypes';

// Helper function to generate unique field key
const getFieldKey = (field, index) => {
  const baseId = field.id || field.field_id;
  return `${baseId}_${index}`;
};

// Helper to get field value with fallback to simple ID for backward compatibility
const getFieldValue = (field, index, formValues) => {
  const compositeKey = getFieldKey(field, index);
  const simpleKey = field.id || field.field_id;
  return formValues[compositeKey] ?? formValues[simpleKey] ?? '';
};

// Helper to convert form values to submission data array format
const convertFormValuesToSubmissionData = (formData) => {
  const fieldMap = new Map();

  Object.entries(formData).forEach(([key, value]) => {
    const parts = key.split('_');
    const lastPart = parts[parts.length - 1];

    let fieldId;
    let hasIndex = false;

    // Check if key has an index suffix (e.g., fieldId_0)
    if (parts.length >= 2 && !isNaN(lastPart) && lastPart === parseInt(lastPart).toString()) {
      fieldId = parts.slice(0, -1).join('_');
      hasIndex = true;
    } else {
      fieldId = key;
    }

    // Prefer composite keys (with index) over simple keys to avoid duplicates
    if (!fieldMap.has(fieldId) || hasIndex) {
      fieldMap.set(fieldId, value);
    }
  });

  return Array.from(fieldMap.entries()).map(([field_id, value]) => ({
    field_id,
    value
  }));
};

const Webform = () => {
  let { id } = useParams();
  const navigate = useNavigate();
  const [formDetails, setFormDetails] = useState([]);
  const [masterDataDropDown, setMasterDataDropDown] = useState([]);
  const [subFormDetails, setSubFormDetails] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [fieldValidationErrors, setFieldValidationErrors] = useState({});
  const [currentFormVersion, setCurrentFormVersion] = useState('1'); // Track form version
  const [formVersionInfo, setFormVersionInfo] = useState(null); // Store version info

  // CSS styles for validation display
  const validationStyles = {
    validationInfo: {
      backgroundColor: '#f8f9fa',
      borderLeft: '3px solid #007bff',
      padding: '8px 12px',
      borderRadius: '4px',
      marginTop: '8px'
    },
    validationHeader: {
      marginBottom: '4px'
    },
    validationRule: {
      display: 'block',
      color: '#6c757d',
      fontSize: '12px',
      marginBottom: '2px',
      lineHeight: '1.4'
    },
    validationIcon: {
      marginRight: '4px'
    },
    validationFeedback: {
      marginTop: '4px',
      padding: '4px 8px',
      borderRadius: '3px',
      fontSize: '12px'
    },
    successFeedback: {
      backgroundColor: '#d4edda',
      borderLeft: '3px solid #28a745'
    },
    errorFeedback: {
      backgroundColor: '#f8d7da',
      borderLeft: '3px solid #dc3545'
    }
  };

  // Formula recalculation function
  const recalculateFormulas = useCallback((currentValues, changedFieldName = null) => {
    if (!formDetails || formDetails.length === 0) return currentValues;

    // Find all formula fields with their indices
    const formulaFieldsWithIndices = formDetails
      .map((field, index) => ({ field, index }))
      .filter(({ field }) => field.fieldtype === 'formula' || field.field_type === 'formula');

    if (formulaFieldsWithIndices.length === 0) return currentValues;

    let updatedValues = { ...currentValues };

    // Recalculate each formula field
    formulaFieldsWithIndices.forEach(({ field: formulaField, index: formulaIndex }) => {
      const expression = formulaField.formula_expression;
      if (!expression) return;

      const referencedFieldIds = formulaField.referenced_field_ids ||
        parseFormulaFields(expression);

      // Build field values map for evaluation
      const fieldValues = {};
      let hasAllValues = true;

      referencedFieldIds.forEach(refId => {
        // Find the referenced field AND its index in formDetails
        // Try multiple field ID resolution strategies for flexibility
        const refFieldIndex = formDetails.findIndex(f => {
          const isNumberField = f.fieldtype === 'number' || f.field_type === 'number';
          if (!isNumberField) return false;

          // Try multiple field ID resolution strategies
          const fieldId = f.field_id || f.id;
          const fieldName = f.field_name || f.fieldName;
          const labelDerivedId = f.label?.toLowerCase().replace(/[^a-z0-9]/g, '_');

          // Match against any of these identifiers
          return fieldId === refId ||
                 fieldName === refId ||
                 labelDerivedId === refId ||
                 f.id === refId;
        });

        if (refFieldIndex !== -1) {
          const refField = formDetails[refFieldIndex];

          // Get value using the referenced field's actual index (composite key)
          const value = getFieldValue(refField, refFieldIndex, updatedValues);

          if (value !== null && value !== undefined && value !== '') {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
              fieldValues[refId] = numValue;
            } else {
              hasAllValues = false;
            }
          } else {
            hasAllValues = false;
          }
        } else {
          // Referenced field not found
          hasAllValues = false;
          console.warn(`Referenced field "${refId}" not found in formDetails`);
        }
      });

      // Calculate if all dependencies have values
      if (hasAllValues && Object.keys(fieldValues).length > 0) {
        const result = evaluateFormula(expression, fieldValues);

        if (result.success) {
          const decimalPlaces = formulaField.decimal_places ?? 2;
          const calculatedValue = parseFloat(result.value.toFixed(decimalPlaces));

          // Store using composite key (formula field's index)
          const formulaFieldKey = getFieldKey(formulaField, formulaIndex);
          updatedValues[formulaFieldKey] = calculatedValue;

          // Debug logging
          console.log('Formula calculated:', {
            formulaField: formulaField.field_name || formulaField.label,
            formulaIndex,
            expression,
            fieldValues,
            result: calculatedValue,
            storedAt: formulaFieldKey
          });
        } else {
          // Clear formula value on calculation error
          const formulaFieldKey = getFieldKey(formulaField, formulaIndex);
          updatedValues[formulaFieldKey] = '';
          console.warn('Formula calculation failed:', {
            formulaField: formulaField.field_name || formulaField.label,
            expression,
            error: result.error,
            clearedAt: formulaFieldKey
          });
        }
      } else {
        // Clear formula value when dependencies are incomplete
        const formulaFieldKey = getFieldKey(formulaField, formulaIndex);
        updatedValues[formulaFieldKey] = '';

        // Only log if not all values are present (common during initial render)
        if (Object.keys(fieldValues).length > 0) {
          console.log('Formula waiting for values:', {
            formulaField: formulaField.field_name || formulaField.label,
            expression,
            receivedValues: fieldValues,
            missingCount: referencedFieldIds.length - Object.keys(fieldValues).length
          });
        }
      }
    });

    return updatedValues;
  }, [formDetails]);

  useEffect(() => {
    getFormDetailsAPI();
    loadFormVersionInfo();
  }, []);

  const loadFormVersionInfo = async () => {
    try {
      const versionInfo = await formTemplateService.getFormVersionInfo(id);
      if (versionInfo.success) {
        setFormVersionInfo(versionInfo.data);
        console.log('Loaded version info:', versionInfo.data);
      }
    } catch (error) {
      console.error('Error loading version info:', error);
    }
  };

  // Initialize formula values when form loads
  useEffect(() => {
    if (formDetails && formDetails.length > 0) {
      const formulaFields = formDetails.filter(f =>
        f.fieldtype === 'formula' || f.field_type === 'formula'
      );

      // Only recalculate if there are formula fields
      if (formulaFields.length > 0) {
        setFormValues(prev => recalculateFormulas(prev));
      }
    }
  }, [formDetails, recalculateFormulas]);

  let getFormDataByMasterAPI = async (dropdownFormId, dropdownFieldId) => {
    try {
      let res = await getFormDataByMasterAction({ dropdownFormId: dropdownFormId, dropdownFieldId: dropdownFieldId });
      if (res.success) {
        setMasterDataDropDown(decryptData(res.data));
      }
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  let getFormDetailsAPI = async () => {
    try {
      setIsLoading(true);
      console.log('Fetching form details for ID:', id);

      // Fetch both form details and version info in parallel
      const [formRes, versionRes] = await Promise.all([
        getFormDetailsAction(id),
        formTemplateService.getFormVersionInfo(id).catch(err => {
          console.warn('Version info fetch failed:', err);
          return { success: false };
        })
      ]);

      let res = formRes;
      console.log('Form details response:', res);

      // Set version if available
      if (versionRes && versionRes.success) {
        setCurrentFormVersion(versionRes.data.latest_version || '1');
        console.log('📌 Loaded version:', versionRes.data.latest_version);
      }
      if (res.success) {
        const formData = res.data.data || res.data;
        console.log('Form data structure:', formData);
        if (formData && formData.fields) {
          const convertedFields = formData.fields.map((field, index) => {
            let fieldTypeId = 1;
            if (field.field_type === 'section' || field.field_type === 'header') {
              fieldTypeId = 17;
            } else if (field.field_type === 'sketch' || field.field_type === 'drawing') {
              fieldTypeId = 18;
            } else if (field.field_type === 'master_data') {
              fieldTypeId = 20;
            } else if (field.field_type === 'radio') {
              fieldTypeId = 3;
            } else if (field.field_type === 'checkbox') {
              fieldTypeId = 4;
            } else if (field.field_type === 'dropdown' || field.field_type === 'select') {
              fieldTypeId = 2;
            } else if (field.field_type === 'number') {
              fieldTypeId = 5;
            } else if (field.field_type === 'email') {
              fieldTypeId = 6;
            } else if (field.field_type === 'password') {
              fieldTypeId = 7;
            } else if (field.field_type === 'date') {
              fieldTypeId = 8;
            } else if (field.field_type === 'time') {
              fieldTypeId = 9;
            } else if (field.field_type === 'datetime') {
              fieldTypeId = 10;
            } else if (field.field_type === 'textarea') {
              fieldTypeId = 11;
            } else if (field.field_type === 'file' || field.field_type === 'file_upload') {
              fieldTypeId = 12;
            } else if (field.field_type === 'signature') {
              fieldTypeId = 13;
            } else if (field.field_type === 'button') {
              fieldTypeId = 14;
            } else if (field.field_type === 'selectManual') {
              fieldTypeId = 15;
            }

            return {
              id: field.field_id || index,
              field_id: field.field_id,
              fieldName: field.label || field.field_name || `Field ${index + 1}`,
              label: field.label,
              field_name: field.field_name,
              fieldtype: field.field_type || 'text',
              field_type: field.field_type,
              fieldTypeId: field.field_type_id || fieldTypeId,
              required: field.required || false,
              placeholder: field.placeholder || null,
              help_text: field.help_text || '',
              options: field.options || [],
              radioCheckboxValue: field.options && field.options.length > 0 ? JSON.stringify(field.options) : null,
              dropdownValue: field.options && field.options.length > 0 ? JSON.stringify(field.options.map(opt => ({ fieldValue: opt }))) : null,
              // Validation-related fields
              validation: field.validation || {},
              min_length: field.min_length || null,
              max_length: field.max_length || null,
              min_value: field.min_value || null,
              max_value: field.max_value || null,
              step: field.step || null,
              // Formula-specific fields
              formula_expression: field.formula_expression,
              referenced_field_ids: field.referenced_field_ids,
              decimal_places: field.decimal_places,
              prefix: field.prefix,
              suffix: field.suffix,
              show_formula: field.show_formula
            };
          });

          let tempFormDetails = [{
            id: formData.form_id || formData.id,
            formName: formData.name || formData.form_name || 'Untitled Form',
            fields: convertedFields
          }];

          if (formData.subforms && formData.subforms.length > 0) {
            const convertedSubFields = formData.subforms.flatMap(subform =>
              subform.fields ? subform.fields.map((field, index) => {
                let fieldTypeId = 1;
                if (field.field_type === 'section' || field.field_type === 'header') {
                  fieldTypeId = 17;
                } else if (field.field_type === 'sketch' || field.field_type === 'drawing') {
                  fieldTypeId = 18;
                } else if (field.field_type === 'master_data') {
                  fieldTypeId = 20;
                } else if (field.field_type === 'radio') {
                  fieldTypeId = 3;
                } else if (field.field_type === 'checkbox') {
                  fieldTypeId = 4;
                } else if (field.field_type === 'dropdown' || field.field_type === 'select') {
                  fieldTypeId = 2;
                } else if (field.field_type === 'number') {
                  fieldTypeId = 5;
                } else if (field.field_type === 'email') {
                  fieldTypeId = 6;
                } else if (field.field_type === 'password') {
                  fieldTypeId = 7;
                } else if (field.field_type === 'date') {
                  fieldTypeId = 8;
                } else if (field.field_type === 'time') {
                  fieldTypeId = 9;
                } else if (field.field_type === 'datetime') {
                  fieldTypeId = 10;
                } else if (field.field_type === 'textarea') {
                  fieldTypeId = 11;
                } else if (field.field_type === 'file' || field.field_type === 'file_upload') {
                  fieldTypeId = 12;
                } else if (field.field_type === 'signature') {
                  fieldTypeId = 13;
                } else if (field.field_type === 'button') {
                  fieldTypeId = 14;
                } else if (field.field_type === 'selectManual') {
                  fieldTypeId = 15;
                }

                return {
                  id: field.field_id || index,
                  fieldName: field.label || field.field_name || `Field ${index + 1}`,
                  fieldtype: field.field_type || 'text',
                  fieldTypeId: field.field_type_id || fieldTypeId,
                  required: field.required || false,
                  placeholder: field.placeholder || null,
                  help_text: field.help_text || '',
                  options: field.options || [],
                  radioCheckboxValue: field.options && field.options.length > 0 ? JSON.stringify(field.options) : null,
                  dropdownValue: field.options && field.options.length > 0 ? JSON.stringify(field.options.map(opt => ({ fieldValue: opt }))) : null,
                  // Validation-related fields
                  validation: field.validation || {},
                  min_length: field.min_length || null,
                  max_length: field.max_length || null,
                  min_value: field.min_value || null,
                  max_value: field.max_value || null,
                  step: field.step || null
                };
              }) : []
            );
            setSubFormDetails(convertedSubFields);
          }

          setFormDetails(convertedFields);
        } else {
          setFormDetails(decryptData(res.data));
        }
      } else {
        toast.error('Failed to load form details');
      }
    } catch (error) {
      console.error('Error fetching form details:', error);
      toast.error('Error loading form structure');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e, item, index) => {
    const value = e.target.value;
    const fieldKey = e.target.name; // Already contains composite key from field rendering
    const originalFieldId = item?.id || fieldKey.split('_').slice(0, -1).join('_');

    if (item?.fieldtype === "checkbox") {
      const existingValues = formValues[fieldKey] || [];
      const updatedValues = e.target.checked
        ? [...existingValues, value]
        : existingValues.filter(v => v !== value);

      setFormValues(prev => {
        const updated = { ...prev, [fieldKey]: updatedValues };
        // Recalculate formulas after field change (using original field ID for formula logic)
        return recalculateFormulas(updated, originalFieldId);
      });

      // Real-time validation for checkbox
      if (item) {
        const errors = validateFieldValue(originalFieldId, updatedValues, item);
        setFieldValidationErrors(prev => ({
          ...prev,
          [originalFieldId]: errors
        }));
      }
    } else {
      setFormValues(prev => {
        const updated = { ...prev, [fieldKey]: value };
        // Recalculate formulas after field change (using original field ID for formula logic)
        return recalculateFormulas(updated, originalFieldId);
      });

      // Real-time validation for other field types
      if (item) {
        const errors = validateFieldValue(originalFieldId, value, item);
        setFieldValidationErrors(prev => ({
          ...prev,
          [originalFieldId]: errors
        }));
      }
    }
  };

  const handleChangeSketch = (name, value) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const renderFieldComponent = (item, index) => {
    const fieldKey = `field-${item.id}-${index}`;

    // Section Header
    if (item.fieldTypeId === 17) {
      return (
        <div key={fieldKey} className="col-12 mb-4">
          <div className="section-header">
            <h4 className="section-title">{item.fieldName}</h4>
            <div className="section-divider"></div>
          </div>
        </div>
      );
    }

    // Button
    if (item.fieldtype === "btn") {
      return (
        <div key={fieldKey} className="col-12 mb-4 text-center">
          <Button variant="primary" size="lg">
            {item.fieldName}
          </Button>
        </div>
      );
    }

    // Regular form fields - render all other field types
    return renderFormField(item, index);
  };

  // Real-time validation function
  const validateFieldValue = (fieldId, value, fieldConfig) => {
    const errors = [];

    // Required validation
    if (fieldConfig.required && (!value || value.toString().trim() === '')) {
      errors.push({
        type: 'required',
        message: fieldConfig.validation?.required_message || 'This field is required',
        severity: 'error'
      });
    }

    // Don't validate other rules if field is empty and not required
    if (!value || value.toString().trim() === '') {
      return errors;
    }

    const stringValue = value.toString();

    // Length validations for text fields
    if (fieldConfig.min_length && stringValue.length < fieldConfig.min_length) {
      errors.push({
        type: 'min_length',
        message: fieldConfig.validation?.min_length_message || `Minimum ${fieldConfig.min_length} characters required`,
        severity: 'error'
      });
    }

    if (fieldConfig.max_length && stringValue.length > fieldConfig.max_length) {
      errors.push({
        type: 'max_length',
        message: fieldConfig.validation?.max_length_message || `Maximum ${fieldConfig.max_length} characters allowed`,
        severity: 'error'
      });
    }

    // Value validations for number fields
    if (fieldConfig.fieldtype === 'number') {
      const numValue = parseFloat(value);

      if (isNaN(numValue)) {
        errors.push({
          type: 'invalid_number',
          message: fieldConfig.validation?.invalid_number_message || 'Please enter a valid number',
          severity: 'error'
        });
      } else {
        if (fieldConfig.min_value !== null && fieldConfig.min_value !== undefined && numValue < fieldConfig.min_value) {
          errors.push({
            type: 'min_value',
            message: fieldConfig.validation?.min_value_message || `Minimum value: ${fieldConfig.min_value}`,
            severity: 'error'
          });
        }

        if (fieldConfig.max_value !== null && fieldConfig.max_value !== undefined && numValue > fieldConfig.max_value) {
          errors.push({
            type: 'max_value',
            message: fieldConfig.validation?.max_value_message || `Maximum value: ${fieldConfig.max_value}`,
            severity: 'error'
          });
        }
      }
    }

    // Email validation
    if (fieldConfig.fieldtype === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(stringValue)) {
        errors.push({
          type: 'invalid_email',
          message: fieldConfig.validation?.invalid_email_message || 'Please enter a valid email address',
          severity: 'error'
        });
      }
    }

    // Pattern validation
    if (fieldConfig.validation?.pattern) {
      const pattern = new RegExp(fieldConfig.validation.pattern);
      if (!pattern.test(stringValue)) {
        errors.push({
          type: 'pattern',
          message: fieldConfig.validation?.pattern_message || 'Please enter a valid format',
          severity: 'error'
        });
      }
    }

    return errors;
  };

  const renderValidationInfo = (item) => {
    if (!item.validation || Object.keys(item.validation).length === 0) {
      return null;
    }

    const validationRules = [];

    // Required field validation
    if (item.required && item.validation.required_message) {
      validationRules.push({
        type: 'required',
        message: item.validation.required_message,
        icon: '⚠️'
      });
    }

    // Length validations
    if (item.min_length) {
      validationRules.push({
        type: 'min_length',
        message: item.validation.min_length_message || `Minimum ${item.min_length} characters required`,
        icon: '📏',
        value: item.min_length
      });
    }

    if (item.max_length) {
      validationRules.push({
        type: 'max_length',
        message: item.validation.max_length_message || `Maximum ${item.max_length} characters allowed`,
        icon: '📏',
        value: item.max_length
      });
    }

    // Value validations for number fields
    if (item.min_value !== null && item.min_value !== undefined) {
      validationRules.push({
        type: 'min_value',
        message: item.validation.min_value_message || `Minimum value: ${item.min_value}`,
        icon: '🔢',
        value: item.min_value
      });
    }

    if (item.max_value !== null && item.max_value !== undefined) {
      validationRules.push({
        type: 'max_value',
        message: item.validation.max_value_message || `Maximum value: ${item.max_value}`,
        icon: '🔢',
        value: item.max_value
      });
    }

    // Pattern validation
    if (item.validation.pattern) {
      validationRules.push({
        type: 'pattern',
        message: item.validation.pattern_message || 'Must match required format',
        icon: '🎯',
        value: item.validation.pattern
      });
    }

    // Field-specific validation messages
    if (item.validation.invalid_email_message && item.fieldtype === 'email') {
      validationRules.push({
        type: 'email',
        message: item.validation.invalid_email_message,
        icon: '📧'
      });
    }

    if (item.validation.invalid_phone_message && item.fieldtype === 'phone') {
      validationRules.push({
        type: 'phone',
        message: item.validation.invalid_phone_message,
        icon: '📱'
      });
    }

    if (item.validation.invalid_url_message && item.fieldtype === 'url') {
      validationRules.push({
        type: 'url',
        message: item.validation.invalid_url_message,
        icon: '🔗'
      });
    }

    if (validationRules.length === 0) {
      return null;
    }

    return (
      <div style={validationStyles.validationInfo}>
        <div style={validationStyles.validationHeader}>
          <small className="text-primary fw-semibold">
            🛡️ Validation Rules:
          </small>
        </div>
        <div className="validation-rules mt-1">
          {validationRules.map((rule, index) => (
            <div key={index} style={validationStyles.validationRule}>
              <span style={validationStyles.validationIcon}>{rule.icon}</span>
              {rule.message}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Extract validation rules for a field
  const getValidationRules = (item) => {
    const validationRules = [];

    // Required field validation
    if (item.required && item.validation.required_message) {
      validationRules.push({
        type: 'required',
        message: item.validation.required_message,
        icon: '⚠️'
      });
    }

    // Length validations
    if (item.min_length) {
      validationRules.push({
        type: 'min_length',
        message: item.validation.min_length_message || `Minimum ${item.min_length} characters required`,
        icon: '📏',
        value: item.min_length
      });
    }

    if (item.max_length) {
      validationRules.push({
        type: 'max_length',
        message: item.validation.max_length_message || `Maximum ${item.max_length} characters allowed`,
        icon: '📏',
        value: item.max_length
      });
    }

    // Value validations for number fields
    if (item.min_value !== null && item.min_value !== undefined) {
      validationRules.push({
        type: 'min_value',
        message: item.validation.min_value_message || `Minimum value: ${item.min_value}`,
        icon: '🔢',
        value: item.min_value
      });
    }

    if (item.max_value !== null && item.max_value !== undefined) {
      validationRules.push({
        type: 'max_value',
        message: item.validation.max_value_message || `Maximum value: ${item.max_value}`,
        icon: '🔢',
        value: item.max_value
      });
    }

    // Field-specific validation messages
    if (item.validation.invalid_email_message && item.fieldtype === 'email') {
      validationRules.push({
        type: 'email',
        message: item.validation.invalid_email_message,
        icon: '📧'
      });
    }

    if (item.validation.invalid_phone_message && item.fieldtype === 'phone') {
      validationRules.push({
        type: 'phone',
        message: item.validation.invalid_phone_message,
        icon: '📱'
      });
    }

    if (item.validation.invalid_url_message && item.fieldtype === 'url') {
      validationRules.push({
        type: 'url',
        message: item.validation.invalid_url_message,
        icon: '🔗'
      });
    }

    return validationRules;
  };

  // Get contextual validation feedback based on current field value
  const getContextualValidationFeedback = (item, fieldValue) => {
    const feedback = [];
    const stringValue = fieldValue ? fieldValue.toString() : '';

    // Required validation
    if (item.required) {
      if (!fieldValue || stringValue.trim() === '') {
        feedback.push({
          type: 'required',
          status: 'error',
          message: item.validation?.required_message || 'This field is required'
        });
        return feedback; // If required and empty, don't check other validations
      } else {
        feedback.push({
          type: 'required',
          status: 'success',
          message: item.validation?.required_message || 'Required field completed'
        });
      }
    }

    // Don't check other validations if field is empty (and not required)
    if (!fieldValue || stringValue.trim() === '') {
      return [];
    }

    // Length validations for text fields
    if (item.min_length) {
      if (stringValue.length < item.min_length) {
        feedback.push({
          type: 'min_length',
          status: 'error',
          message: item.validation?.min_length_message || `Minimum ${item.min_length} characters required`
        });
      } else {
        feedback.push({
          type: 'min_length',
          status: 'success',
          message: item.validation?.min_length_message || `Minimum ${item.min_length} characters required`
        });
      }
    }

    if (item.max_length) {
      if (stringValue.length > item.max_length) {
        feedback.push({
          type: 'max_length',
          status: 'error',
          message: item.validation?.max_length_message || `Maximum ${item.max_length} characters allowed`
        });
      } else if (stringValue.length > 0) {
        feedback.push({
          type: 'max_length',
          status: 'success',
          message: item.validation?.max_length_message || `Maximum ${item.max_length} characters allowed`
        });
      }
    }

    // Value validations for number fields
    if (item.fieldtype === 'number' && fieldValue) {
      const numValue = parseFloat(fieldValue);

      if (isNaN(numValue)) {
        feedback.push({
          type: 'invalid_number',
          status: 'error',
          message: item.validation?.invalid_number_message || 'Please enter a valid number'
        });
      } else {
        // Min value validation
        if (item.min_value !== null && item.min_value !== undefined) {
          if (numValue < item.min_value) {
            feedback.push({
              type: 'min_value',
              status: 'error',
              message: item.validation?.min_value_message || `Minimum value: ${item.min_value}`
            });
          } else {
            feedback.push({
              type: 'min_value',
              status: 'success',
              message: item.validation?.min_value_message || `Minimum value: ${item.min_value}`
            });
          }
        }

        // Max value validation
        if (item.max_value !== null && item.max_value !== undefined) {
          if (numValue > item.max_value) {
            feedback.push({
              type: 'max_value',
              status: 'error',
              message: item.validation?.max_value_message || `Maximum value: ${item.max_value}`
            });
          } else {
            feedback.push({
              type: 'max_value',
              status: 'success',
              message: item.validation?.max_value_message || `Maximum value: ${item.max_value}`
            });
          }
        }
      }
    }

    // Email validation
    if (item.fieldtype === 'email' && fieldValue) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(stringValue)) {
        feedback.push({
          type: 'email',
          status: 'error',
          message: item.validation?.invalid_email_message || 'Please enter a valid email address'
        });
      } else {
        feedback.push({
          type: 'email',
          status: 'success',
          message: item.validation?.invalid_email_message || 'Valid email address'
        });
      }
    }

    return feedback;
  };

  // Real-time validation message display
  const renderRealTimeValidation = (item, index) => {
    const fieldValue = getFieldValue(item, index, formValues);

    // Only show validation feedback if user has started typing
    if (!fieldValue || fieldValue.toString().trim() === '') {
      return null;
    }

    // Get contextual validation feedback based on current field value
    const contextualFeedback = getContextualValidationFeedback(item, fieldValue);

    if (contextualFeedback.length === 0) {
      return null;
    }

    return (
      <div style={{ ...validationStyles.validationFeedback }}>
        {contextualFeedback.map((feedback, index) => (
          <small
            key={index}
            className={feedback.status === 'error' ? 'text-danger d-block' : 'text-success d-block'}
            style={feedback.status === 'error' ? validationStyles.errorFeedback : validationStyles.successFeedback}
          >
            {feedback.status === 'error' ? '❌' : '✅'} {feedback.message}
          </small>
        ))}
      </div>
    );
  };

  const renderFormField = (item, index) => {
    const fieldKey = `field-${item.id}-${index}`;
    const fieldType = item.fieldtype || item.field_type || 'text';

    return (
      <div key={fieldKey} className={fieldType === 'markdown' ? "col-12 mb-4" : "col-lg-6 col-md-6 col-sm-12 mb-4"}>
        <div className="form-field-container">
          <Form.Group>
            {/* Skip label for markdown - it renders its own formatted content */}
            {fieldType !== 'markdown' && (
              <Form.Label className="field-label">
                {item.fieldName}
                {item.required && <span className="required-asterisk">*</span>}
              </Form.Label>
            )}

            {item.help_text && (
              <div className="field-help-text">{item.help_text}</div>
            )}

            {renderInputComponent(item, index)}
            {renderValidationInfo(item)}
            {renderRealTimeValidation(item, index)}
          </Form.Group>
        </div>
      </div>
    );
  };

  const renderInputComponent = (item, index) => {
    // Normalize field type to handle both fieldtype and field_type
    const fieldType = item.fieldtype || item.field_type || 'text';

    switch (fieldType) {
      case "select":
      case "dropdown":
        return (
          <Form.Select
            id={item.id}
            name={getFieldKey(item, index)}
            data-field-id={item.id}
            value={getFieldValue(item, index, formValues)}
            onChange={(e) => handleChange(e, item, index)}
            className="modern-select"
            required={item.required}
          >
            <option value="">Choose an option...</option>
            {item.dropdownValue && JSON.parse(item.dropdownValue).map((option, idx) => (
              <option key={idx} value={option.fieldValue}>
                {option.fieldValue}
              </option>
            ))}
          </Form.Select>
        );

      case "radio":
        return (
          <div className="radio-group">
            {item.radioCheckboxValue && JSON.parse(item.radioCheckboxValue).map((option, idx) => (
              <div key={idx} className="form-check mb-2">
                <Form.Check
                  type="radio"
                  id={`${item.id}-${idx}`}
                  name={getFieldKey(item, index)}
                  data-field-id={item.id}
                  value={option}
                  checked={getFieldValue(item, index, formValues) === option}
                  onChange={(e) => handleChange(e, item, index)}
                  label={option}
                  className="modern-radio"
                />
              </div>
            ))}
          </div>
        );

      case "checkbox":
      case "checkboxes":
        return (
          <div className="checkbox-group">
            {item.radioCheckboxValue && JSON.parse(item.radioCheckboxValue).map((option, idx) => {
              const fieldValue = getFieldValue(item, index, formValues);
              const isChecked = Array.isArray(fieldValue) ? fieldValue.includes(option) : false;
              return (
                <div key={idx} className="form-check mb-2">
                  <Form.Check
                    type="checkbox"
                    id={`${item.id}-${idx}`}
                    name={getFieldKey(item, index)}
                    data-field-id={item.id}
                    value={option}
                    checked={isChecked}
                    onChange={(e) => handleChange(e, item, index)}
                    label={option}
                    className="modern-checkbox"
                  />
                </div>
              );
            })}
          </div>
        );

      case "textarea":
        return (
          <Form.Control
            as="textarea"
            rows={4}
            id={item.id}
            name={getFieldKey(item, index)}
            data-field-id={item.id}
            value={getFieldValue(item, index, formValues)}
            onChange={(e) => handleChange(e, item, index)}
            placeholder={item.placeholder || `Enter ${item.fieldName}...`}
            className="modern-textarea"
            required={item.required}
            minLength={item.min_length || undefined}
            maxLength={item.max_length || undefined}
            title={item.validation?.required_message || undefined}
          />
        );

      case "paragraph":
        return (
          <Form.Control
            as="textarea"
            rows={5}
            id={item.id}
            name={getFieldKey(item, index)}
            data-field-id={item.id}
            value={getFieldValue(item, index, formValues)}
            onChange={(e) => handleChange(e, item, index)}
            placeholder={item.placeholder || `Enter ${item.fieldName}...`}
            className="modern-textarea"
            required={item.required}
            minLength={item.min_length || undefined}
            maxLength={item.max_length || undefined}
            title={item.validation?.required_message || undefined}
          />
        );

      case "file":
      case "file_upload":
        return (
          <div className="file-upload-container">
            <Form.Control
              type="file"
              id={item.id}
              name={getFieldKey(item, index)}
              data-field-id={item.id}
              onChange={(e) => handleChange(e, item, index)}
              className="modern-file-input"
              required={item.required}
              accept="*/*"
            />
            <div className="file-upload-help">
              <small className="text-muted">
                Click to select a file or drag and drop
              </small>
            </div>
          </div>
        );

      case "signature":
        return (
          <SignatureField
            field={{
              field_id: getFieldKey(item, index),
              label: item.label || 'Signature',
              required: item.required || item.is_required || false,
              help_text: item.help_text,
              validation: item.validation || {}
            }}
            value={getFieldValue(item, index, formValues)}
            onChange={handleChangeSketch}
            error={fieldValidationErrors[getFieldKey(item, index)]}
          />
        );

      case "date":
        return (
          <Form.Control
            type="date"
            id={item.id}
            name={getFieldKey(item, index)}
            data-field-id={item.id}
            value={getFieldValue(item, index, formValues)}
            onChange={(e) => handleChange(e, item, index)}
            className="modern-input"
            required={item.required}
          />
        );

      case "time":
        return (
          <Form.Control
            type="time"
            id={item.id}
            name={getFieldKey(item, index)}
            data-field-id={item.id}
            value={getFieldValue(item, index, formValues)}
            onChange={(e) => handleChange(e, item, index)}
            className="modern-input"
            required={item.required}
          />
        );

      case "datetime":
      case "datetime-local":
        return (
          <Form.Control
            type="datetime-local"
            id={item.id}
            name={getFieldKey(item, index)}
            data-field-id={item.id}
            value={getFieldValue(item, index, formValues)}
            onChange={(e) => handleChange(e, item, index)}
            className="modern-input"
            required={item.required}
          />
        );

      case "number":
        return (
          <Form.Control
            type="number"
            id={item.id}
            name={getFieldKey(item, index)}
            data-field-id={item.id}
            value={getFieldValue(item, index, formValues)}
            onChange={(e) => handleChange(e, item, index)}
            placeholder={item.placeholder || `Enter ${item.fieldName}...`}
            className="modern-input"
            required={item.required}
            min={item.min_value || undefined}
            max={item.max_value || undefined}
            step={item.step || undefined}
            title={item.validation?.invalid_number_message || item.validation?.required_message || undefined}
          />
        );

      case "email":
        return (
          <Form.Control
            type="email"
            id={item.id}
            name={getFieldKey(item, index)}
            data-field-id={item.id}
            value={getFieldValue(item, index, formValues)}
            onChange={(e) => handleChange(e, item, index)}
            placeholder={item.placeholder || "Enter email address..."}
            className="modern-input"
            required={item.required}
            pattern={item.validation?.pattern || undefined}
            title={item.validation?.invalid_email_message || item.validation?.pattern_message || item.validation?.required_message || undefined}
          />
        );

      case "password":
        return (
          <Form.Control
            type="password"
            id={item.id}
            name={getFieldKey(item, index)}
            data-field-id={item.id}
            value={getFieldValue(item, index, formValues)}
            onChange={(e) => handleChange(e, item, index)}
            placeholder={item.placeholder || "Enter password..."}
            className="modern-input"
            required={item.required}
          />
        );

      case "sketch":
      case "drawing":
        return (
          <Sketch
            fieldName={item.fieldName}
            id={item.id}
            handleChangeSketch={handleChangeSketch}
          />
        );

      case "selectManual":
      case "select_manual":
        return (
          <Form.Select
            id={item.id}
            name={item.id}
            onChange={handleChange}
            className="modern-select"
            required={item.required}
          >
            <option value="">Choose an option...</option>
            {item.dropdownValue && JSON.parse(item.dropdownValue).map((option, idx) => (
              <option key={idx} value={option.fieldValue}>
                {option.fieldValue}
              </option>
            ))}
            {masterDataDropDown.map((option, idx) => (
              <option key={`master-${idx}`} value={option.id}>
                {option.Name}
              </option>
            ))}
          </Form.Select>
        );

      case "button":
      case "btn":
        return (
          <Button
            variant="outline-primary"
            size="lg"
            className="w-100"
            onClick={() => console.log(`Button ${item.fieldName} clicked`)}
          >
            {item.fieldName}
          </Button>
        );

      case "header":
      case "section":
        // This is handled in renderFieldComponent, should not reach here
        return null;

      case "formula": {
        const expression = item.formula_expression || '';
        const prefix = item.prefix || '';
        const suffix = item.suffix || '';
        const showFormula = item.show_formula || false;

        // Use getFieldValue with index to support composite keys
        const rawValue = getFieldValue(item, index, formValues);
        const fieldValue = (rawValue !== undefined && rawValue !== null && rawValue !== '')
          ? rawValue
          : '';

        return (
          <div className="formula-field-display">
            <div className="input-group">
              {prefix && (
                <span className="input-group-text bg-light">{prefix}</span>
              )}
              <Form.Control
                type="text"
                value={fieldValue}
                readOnly
                disabled
                className="bg-light text-dark"
                placeholder="Calculating..."
                style={{ cursor: 'not-allowed' }}
              />
              {suffix && (
                <span className="input-group-text bg-light">{suffix}</span>
              )}
            </div>

            {showFormula && expression && (
              <Form.Text className="text-muted font-monospace d-block mt-2">
                <small>
                  <strong>Formula:</strong> {expression}
                </small>
              </Form.Text>
            )}
          </div>
        );
      }

      case "markdown":
        // Markdown is display-only, render as formatted content (not input)
        return (
          <div className="markdown-field-container mb-3">
            <div className="markdown-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ node, ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer" />
                  ),
                  img: ({ node, ...props }) => (
                    <img {...props} style={{ maxWidth: '100%', height: 'auto' }} loading="lazy" />
                  ),
                }}
              >
                {item.label || item.fieldName || ''}
              </ReactMarkdown>
            </div>
          </div>
        );

      case "text":
      default:
        return (
          <Form.Control
            type="text"
            id={item.id}
            name={getFieldKey(item, index)}
            data-field-id={item.id}
            value={getFieldValue(item, index, formValues)}
            onChange={(e) => handleChange(e, item, index)}
            placeholder={item.placeholder || `Enter ${item.fieldName}...`}
            className="modern-input"
            required={item.required}
            minLength={item.min_length || undefined}
            maxLength={item.max_length || undefined}
            pattern={item.validation?.pattern || undefined}
            title={item.validation?.pattern_message || item.validation?.required_message || undefined}
          />
        );
    }
  };

  const renderFormFields = () => {
    if (!formDetails || formDetails.length === 0) {
      return (
        <div className="text-center py-5">
          <div className="no-fields-message">
            <h5 className="text-muted">No form fields found</h5>
            <p className="text-muted mb-0">This form may be empty or there was an error loading the structure.</p>
          </div>
        </div>
      );
    }

    return (
      <Row className="g-4">
        {formDetails.map((item, index) => renderFieldComponent(item, index))}

        {/* Render sub-form fields if they exist */}
        {subFormDetails && subFormDetails.length > 0 && (
          <>
            <div className="col-12 my-4">
              <div className="subform-divider">
                <h5 className="subform-title">Additional Information</h5>
              </div>
            </div>
            {subFormDetails.map((item, index) => renderFieldComponent(item, `sub-${index}`))}
          </>
        )}
      </Row>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields directly from formValues (supports composite keys)
    const errors = [];
    const requiredFields = formDetails.filter(field => field.required);

    requiredFields.forEach(field => {
      // Check if any composite key variant of this field has a value
      const fieldId = field.id;
      const hasValue = Object.keys(formValues).some(key => {
        // Match exact field id or composite key (fieldId_0, fieldId_1, etc.)
        if (key === fieldId || key.startsWith(`${fieldId}_`)) {
          const value = formValues[key];
          return value && !(Array.isArray(value) && value.length === 0) && value.toString().trim() !== '';
        }
        return false;
      });

      if (!hasValue) {
        errors.push(field.fieldName);
      }
    });

    // Check subform required fields if they exist
    if (subFormDetails && subFormDetails.length > 0) {
      const requiredSubFields = subFormDetails.filter(field => field.required);
      requiredSubFields.forEach(field => {
        const fieldId = field.id;
        const hasValue = Object.keys(formValues).some(key => {
          if (key === fieldId || key.startsWith(`${fieldId}_`)) {
            const value = formValues[key];
            return value && !(Array.isArray(value) && value.length === 0) && value.toString().trim() !== '';
          }
          return false;
        });

        if (!hasValue) {
          errors.push(field.fieldName);
        }
      });
    }

    if (errors.length > 0) {
      toast.error(`Please fill in the following required fields: ${errors.join(', ')}`);
      return;
    }

    // All validations passed
    toast.success('✅ Form validation successful! All required fields are completed.');

    // Convert to submission format for logging
    const submissionData = convertFormValuesToSubmissionData(formValues);
    console.log('Form validation passed. Submission data:', submissionData);
  };

  return (
    <>
      <Dashboardheader />
      <div className="wrapper">
        <Sidebar />
        <div id="content">
          <Container fluid className="px-5 pt-5">
            <Row>
              <Col lg={12} md={12} sm={12} className="">
                <div className="page-header mb-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h2 className="page-title">
                        {formDetails.length > 0 ? formDetails[0].formName : "Form Preview"}
                      </h2>
                      <p className="page-subtitle">Review and test your form</p>
                    </div>
                    <div className="d-flex gap-3">
                      <Link
                        to={`${config.baseUrl}editWebForm/${id}?version=${currentFormVersion}`}
                        className="btn btn-outline-primary"
                      >
                        <i className="fas fa-edit me-2"></i>Edit Form
                      </Link>
                      <Link to={`${config.baseUrl}webforms`} className="btn btn-outline-secondary">
                        <BsArrowLeftCircleFill className="me-2" />Back to Forms
                      </Link>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>

          <Container className="py-4">
            <Row className="justify-content-center">
              <Col lg={10} xl={8}>
                <div className="modern-form-preview">
                  {isLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3 text-muted">Loading form structure...</p>
                    </div>
                  ) : (
                    <Card className="form-preview-card border-0 shadow-sm">
                      <Card.Header className="form-header bg-white border-0 py-4">
                        <div className="text-center">
                          <h3 className="form-title mb-2 text-dark fw-bold">
                            {formDetails.length > 0 ? formDetails[0].formName : "Form Preview"}
                          </h3>
                          <p className="form-subtitle text-muted mb-0">Please fill out all required fields below</p>
                        </div>
                      </Card.Header>
                      <Card.Body className="form-body p-4 p-md-5">
                        <Form onSubmit={handleSubmit} className="modern-form" noValidate>
                          <div className="form-sections">
                            {renderFormFields()}
                          </div>
                          <div className="form-actions mt-5 pt-4 border-top">
                            <div className="d-flex justify-content-center gap-3">
                              <Button
                                variant="outline-secondary"
                                size="lg"
                                className="px-4"
                                onClick={() => navigate(`${config.baseUrl}webforms`)}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                className="px-5"
                              >
                                Validate Form
                              </Button>
                            </div>
                          </div>
                        </Form>
                      </Card.Body>
                    </Card>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </>
  );
};

export default Webform;